import { useState, FormEvent, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { makeApiCall } from "src/api/apiRequests";
import API_HEADERS from "src/api/apiConfig";
import API_PATHS from "src/api/apiPaths";
import FLASH_MESSAGES from "src/helpers/FlashMessages";
import ValidationMessage from "src/helpers/ValidationMessage";

interface EditCategoryProps {
  onShowModal: boolean;
  setShowModal: (visible: boolean) => void;
  categoryId: number;
  setModified: (visible: boolean) => void;
}

type Item = {
  title: string;
  type: string;
  id?: number;
};

const EditCategory: React.FC<EditCategoryProps> = ({
  onShowModal,
  setShowModal,
  categoryId,
  setModified,
}) => {
  const { t } = useTranslation();

  const [title, setTitle] = useState("");
  const [documents, setDocuments] = useState<Item[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingButton, setLoadingButton] = useState(false);
  const [validations, setValidations] = useState<Record<string, string>>({});

  const onClose = () => {
    setShowModal(false);
  };

  const nurseryDocuments = documents.filter(
    (item: any) => item.type === "cerdhe"
  );
  const kindergartenDocuments = documents.filter(
    (item: any) => item.type === "kopesht"
  );

  // Find last indices safely
  const findLastIndexOfType = (type: string) => {
    const filteredDocs = documents.filter((doc) => doc.type === type);
    if (filteredDocs.length === 0) return -1;
    const lastItem = filteredDocs.pop();
    return documents.lastIndexOf(lastItem as any);
  };

  const lastCerdheIndex = findLastIndexOfType("cerdhe");
  const lastKopeshtIndex = findLastIndexOfType("kopesht");

  const getCategoryData = async (): Promise<void> => {
    try {
      const response: any = await makeApiCall<ResponseType>(
        API_PATHS.categoryData(categoryId),
        "GET",
        API_HEADERS.authenticated
      );

      setTitle(response.childrenCategory.title);

      var docs: any = [];

      var childrenCategoryDocs =
        response.childrenCategory.children_category_documents;

      childrenCategoryDocs.forEach((document: any) => {
        docs.push({
          title: document.title,
          type: document.type,
          id: document.id,
        });
      });
      setDocuments(docs);

      setLoading(false);
    } catch (error: any) {
      FLASH_MESSAGES.errorMsg(error.response.data.error);
      onClose();
    }
  };

  useEffect(() => {
    getCategoryData();
  }, []);

  const addNewDocument = (type: string) => {
    const newItems: Item[] = [...documents, { title: "", type: type }];
    setDocuments(newItems);
  };

  const addButton = (type: string) => {
    let addRowButton = (
      <button
        type="button"
        className="button-white has-icon only"
        onClick={() => addNewDocument(type)}
      >
        <i className="icon simple-plus-icon"></i>
      </button>
    );

    return addRowButton;
  };

  const removeDocument = (type: string, index: number) => {
    setDocuments((prevItems) => prevItems.filter((item, idx) => idx !== index));
  };

  // This function generates a button component that, when clicked, removes a document of a specified type at a given index.
  const removeButton = (type: string, index: number) => {
    let removeRowButton = (
      <button
        type="button"
        className="button-white has-icon only"
        onClick={() => removeDocument(type, index)}
      >
        <i className="icon minus-icon"></i>
      </button>
    );
    return removeRowButton;
  };

  const handleChange = (value: any, i: number) => {
    let targetArray = documents;
    const updatedArray = [...targetArray];
    updatedArray[i].title = value;

    setDocuments(updatedArray);
  };

  const editCategory = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setLoadingButton(true);

    const categoryData = {
      title: title,
      document: { ...documents },
    };
    try {
      const response: any = await makeApiCall<ResponseType>(
        API_PATHS.editCategory(categoryId),
        "PUT",
        API_HEADERS.authenticated,
        categoryData
      );

      FLASH_MESSAGES.successMsg(response.success);

      setModified(true);

      onClose();

      setLoadingButton(false);
    } catch (error: any) {
      if (error.response.status === 422) {
        setValidations(error.response.data);
      } else {
        FLASH_MESSAGES.errorMsg(error.response.data.error);
        onClose();
      }
      setLoadingButton(false);
    }
  };

  return (
    <Modal
      isOpen={onShowModal && !loading}
      toggle={() => onClose()}
      size="md"
      centered
    >
      <ModalHeader
        className="text-dark font-size-15"
        toggle={() => onClose()}
        tag="span"
      >
        {t("childrens_categories_edit_title")}
      </ModalHeader>
      <form onSubmit={(e) => editCategory(e)}>
        <ModalBody>
          <div className="modal-note">
            {t("childrens_categories_edit_desc")}
          </div>
          <div className="form-block">
            <label className="required">
              {t("childrens_categories_edit_name_label")}
            </label>
            <input
              type="text"
              name="name"
              placeholder={t("childrens_categories_edit_name_placeholder")}
              value={title}
              className={
                "form-input" + (validations.title ? " input-error" : "")
              }
              autoComplete="off"
              onChange={(e) => setTitle(e.target.value)}
            />
            {validations.title && (
              <ValidationMessage message={validations.title[0]} />
            )}
          </div>
          <div className="form-block mb-0">
            <label className="required">
              {t("childrens_categories_nursery_label")}
            </label>
          </div>
          {documents.map(
            (document, index) =>
              document.type === "cerdhe" && (
                <div key={index} className="documents">
                  <div className="form-block">
                    <div className="input-row">
                      <input
                        type="text"
                        name="title"
                        placeholder={t(
                          "childrens_categories_document_name_placeholder"
                        )}
                        value={document.title ? document.title : ""}
                        className={
                          "form-input" +
                          (validations[`document.${index}.title`]
                            ? " input-error"
                            : "")
                        }
                        autoComplete="off"
                        onChange={(e) => handleChange(e.target.value, index)}
                      />
                      {removeButton("cerdhe", index)}
                    </div>
                    {validations[`document.${index}.title`] && (
                      <ValidationMessage
                        message={validations[`document.${index}.title`]}
                      />
                    )}
                  </div>
                  <div className="add-remove-buttons">
                    {index === lastCerdheIndex && addButton("cerdhe")}
                  </div>
                </div>
              )
          )}
          {nurseryDocuments.length === 0 && (
            <div className="documents">
              <div className="add-remove-buttons">{addButton("cerdhe")}</div>
            </div>
          )}
          <div className="form-block mb-0">
            <label className="required">
              {t("childrens_categories_kindergarten_label")}
            </label>
          </div>
          {documents.map(
            (document, index) =>
              document.type === "kopesht" && (
                <div key={index} className="documents">
                  <div className="form-block">
                    <div className="input-row">
                      <input
                        type="text"
                        name="title"
                        placeholder={t(
                          "childrens_categories_document_name_placeholder"
                        )}
                        value={document.title ? document.title : ""}
                        className={
                          "form-input" +
                          (validations[`document.${index}.title`]
                            ? " input-error"
                            : "")
                        }
                        autoComplete="off"
                        onChange={(e) => handleChange(e.target.value, index)}
                      />
                      {removeButton("kopesht", index)}
                    </div>
                    {validations[`document.${index}.title`] && (
                      <ValidationMessage
                        message={validations[`document.${index}.title`]}
                      />
                    )}
                  </div>
                  <div className="add-remove-buttons">
                    {index === lastKopeshtIndex && addButton("kopesht")}
                  </div>
                </div>
              )
          )}
          {kindergartenDocuments.length === 0 && (
            <div className="documents">
              <div className="add-remove-buttons">{addButton("kopesht")}</div>
            </div>
          )}
          <div className="form-block">
            {validations.document && (
              <ValidationMessage message={validations.document[0]} />
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <button
            className="button-white"
            type="button"
            onClick={() => onClose()}
          >
            {t("childrens_categories_edit_cancel")}
          </button>

          {loadingButton ? (
            <button type="button" className="button-blue has-icon">
              <i className="spinner-icon" />
              {t("childrens_categories_edit_adding_button")}
            </button>
          ) : (
            <button type="submit" className="button-blue has-icon">
              <i className="icon edit-icon" />
              {t("childrens_categories_edit_add_button")}
            </button>
          )}
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default EditCategory;
